import React, { memo, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { BaseRes } from 'bff/models/base/baseRes';
import { TravelWarning, TravelWarnings } from 'bff/models/info';
import { GeoAreaId } from 'constants/enums/geoAreaId.enum';
import { TravelPlanType } from 'constants/enums/travelPlanType.enum';
// import { TravelSchengen } from 'constants/enums/travelSchengen.enum';
import apiService from 'bff/services/apiService';
import commonService from 'utils/commonService';
import CheckItem from './CheckItem';
import { GroupVM, IProps, ItemVM } from './types';

const styles: {[key: string]: React.CSSProperties } = {
  inline: {
    position: 'relative',
    // width: 374,
    minHeight: 40,
    height: 'auto',
    paddingBottom: 5
  }
};

const FilterSelect: React.FC<IProps> = ({ isDomestic, isBroad, prodId, maxCount, selected, selected2, disabled, children, onItemAdd, onItemRemove, ...props }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const filterSelectRef = useRef<HTMLDivElement | null>(null);
  const [dataSource, setDataSource] = useState<TravelWarning[]>([]);
  const [tabOptions, setTabOptions] = useState<{ value: string, text: string; }[]>([]);
  const [currentTab, setCurrentTab] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<{ groupId: string; itemId: string; itemName: string; }[]>([]);
  const [groups, setGroups] = useState<GroupVM[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [result, setResult] = useState<{ groupId: string; itemId: string; itemName: string; isChecked: boolean; }[]>([]);

  /**
   * @description Component unmount cleanup
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description 取得旅遊地點 data model
   */
  useEffect(() => {
    (async () => {
      const baseReq = commonService.getApiReqData();
      const reqConfig = commonService.getReqConfig();
      const response: BaseRes<TravelWarnings> = await apiService.fetchTravelwarning(baseReq, reqConfig);
      if (response && response.TRANRS && response.TRANRS.GEOCOUNTRYS) {
        const result = commonService.arrayTypeReturn(response.TRANRS.GEOCOUNTRYS);
        if (result && !_.isEqual(result, dataSource)) {
          setDataSource(result);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description 關閉面版後，清除關鍵字查詢
   */
  useEffect(() => {
    if (!isOpen) {
      setKeyword('');
    }
  }, [isOpen]);

  /**
   * @description 若關閉面版，重置第一個頁籤
   */
  useEffect(() => {
    if (!isOpen && tabOptions && tabOptions.length > 0) {
      setCurrentTab(tabOptions[0].value);

      // 申根保險以歐洲為預設
      if (prodId === TravelPlanType.NTAG) setCurrentTab('EU');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tabOptions]);

  /**
   * @description 正規化資料為 view model (不處理泡泡險邏輯)
   */
  useEffect(() => {
    let nzData: TravelWarning[] = [];
    // 為國內保險
    if (isDomestic) {
      nzData = _.filter(dataSource,
        (travelWarning: TravelWarning) => travelWarning.GEOAREA_ID === GeoAreaId.Domestic);
    }
    // 為國外保險
    if (isBroad) {
      nzData = _.filter(dataSource,
        (travelWarning: TravelWarning) => travelWarning.GEOAREA_ID !== GeoAreaId.Domestic);
      // 判斷是否為申根保險
      // 申根保險開放全部國家 (2024/12/5)
      // if (prodId === TravelPlanType.NTAG) {
      //   nzData = _.filter(nzData, (travelWarning: TravelWarning) => travelWarning.GEOAREA_ID === GeoAreaId.EU);
      //   nzData.map(it => {
      //     it.COUNTRYS = it.COUNTRYS.filter(it => it.SCHENGEN === TravelSchengen.Yes);
      //     return it;
      //   });
      // }
    }

    const options = nzData.map(it => ({ value: it.GEOAREA_ID, text: it.GEOAREA_NAME }));
    // 轉換為 view model
    const selectedItems: { groupId: string; itemId: string; itemName: string; }[] = [];
    const hotCountries: ItemVM[] = [];
    const groups = nzData.map(grp => {
      return {
        groupId: grp.GEOAREA_ID,
        groupName: grp.GEOAREA_NAME,
        items: grp.COUNTRYS.map(it => {
          const find = selected2?.find(_it => _it.COUNTRY_ID === it.COUNTRY_ID);
          if (find) {
            selectedItems.push({
              groupId: grp.GEOAREA_ID,
              itemId: it.COUNTRY_ID,
              itemName: it.COUNTRY_NAME
            });
          }
          const country: ItemVM = {
            groupId: grp.GEOAREA_ID,
            itemId: it.COUNTRY_ID,
            itemName: it.COUNTRY_NAME,
            isChecked: !!find,
            sort: it.HOT_COUNTRY ? +it.HOT_COUNTRY : 0
          };
          if (it.HOT_COUNTRY) {
            hotCountries.push(country);
          }
          return country;
        })
      };
    });
    // 追加「熱門地點」群組
    // 申根保險不需「熱門地點」群組
    if (prodId !== TravelPlanType.NTAG && hotCountries.length > 0) {
      groups.unshift({
        groupId: 'HOT',
        groupName: '熱門國家',
        items: hotCountries.sort((a, b) => a.sort - b.sort)
      });
      options.unshift({
        value: 'HOT',
        text: '熱門國家'
      });
    }
    // 依選擇順序呈現
    const orderedItems: { groupId: string; itemId: string; itemName: string; }[] = [];
    selected2.forEach(it => {
      const find = selectedItems.find(_it => _it.itemId === it.COUNTRY_ID);
      if (find) orderedItems.push(find);
    });
    setSelectedItems(orderedItems);
    setTabOptions(options);
    setGroups(groups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, isDomestic, isBroad, prodId, selected2]);

  /**
   * @description 查詢國家
   */
  useEffect(() => {
    if (keyword && groups) {
      const _result: { groupId: string; itemId: string; itemName: string; isChecked: boolean; }[] = [];
      groups.forEach(grp => {
        grp.items.forEach(it => {
          const find = _result.find(_it => _it.itemId === it.itemId);
          // 重複的國家不需加入
          if (!find && it.itemName.includes(keyword)) {
            _result.push({
              groupId: it.groupId,
              itemId: it.itemId,
              itemName: it.itemName,
              isChecked: it.isChecked
            });
          }
        });
      });
      setResult(_result);
    } else {
      setResult([]);
    }
  }, [keyword, groups]);

  /**
   * @description 處理點擊組件外區域的事件
   * @param event event object
   */
  const handleClickOutside = (event: any) => {
    // 說明: 若有警示訊息，則不作任何動作
    const swalElem = document.getElementsByClassName('swal2-container');
    if (disabled || swalElem.length > 0) return;
    if (filterSelectRef.current && !filterSelectRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  /**
   * @description 處理關鍵字 onChange 執行的事件
   * @param event event object
   */
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  /**
   * @description 處理關鍵字 onClick 執行的事件
   * @param event event object
   */
  const handleSearchClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    // 說明: 若有警示訊息，則不作任何動作
    const swalElem = document.getElementsByClassName('swal2-container');
    if (isOpen || disabled || swalElem.length > 0) return;
    setIsOpen(true);
  };

  /**
   * @description 清除關鍵字
   * @param event event object
   */
  const handleSearchClear = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    // 說明: 若有警示訊息，則不作任何動作
    const swalElem = document.getElementsByClassName('swal2-container');
    if (disabled || swalElem.length > 0) return;
    setKeyword('');
  };

  /**
   * @description 開啟/關閉選擇面板
   */
  const handleSelectClick = () => {
    // 說明: 若有警示訊息，則不作任何動作
    const swalElem = document.getElementsByClassName('swal2-container');
    if (disabled || swalElem.length > 0) return;
    setIsOpen(prev => !prev);
  };

  /**
   * @description 執行標籤頁面版切換
   * @param event event object
   * @param tab 標籤
   */
  const handleTabClick = (event: React.MouseEvent<HTMLLIElement>, tab: string) => {
    event.stopPropagation();
    setCurrentTab(tab);
  };

  /**
   * @description 移除勾選項目
   * @param event event object
   * @param groupId 群組編號
   * @param itemId 項目編號
   */
  const handleItemDelete = (event: React.MouseEvent<HTMLButtonElement>, groupId: string, itemId: string) => {
    event.stopPropagation();
    event.preventDefault();
    if (onItemRemove) onItemRemove(groupId, itemId);
  };

  /**
   * @description 新增勾選項目
   * @param groupId 群組編號
   * @param itemId 項目編號
   */
  const handleItemClick = (groupId: string, itemId: string) => {
    // 若未選擇則勾選，已選擇則移除
    if (selectedItems.find(it => it.itemId === itemId)) {
      if (onItemRemove) onItemRemove(groupId, itemId);
    } else {
      // 已選地點加上當前勾選地點不得超過限制大小 (最多6項)
      if (selectedItems.length + 1 > maxCount) return;
      if (onItemAdd) onItemAdd(groupId, itemId);
    }
  };

  return (
    <>
      <div style={styles.inline} ref={filterSelectRef} className={'element__select filter-select' + (disabled ? ' filter-select--disabled' : '')} onClick={handleSelectClick}>
        {selectedItems.map((it, index) => (
          <div key={`selected-${index}`} className="element__tag selected">
            {!disabled && (
              <button type="button" className="element__tag-btn" onClick={(e) => handleItemDelete(e, it.groupId, it.itemId)} />
            )}
            <span onClick={(e) => e.stopPropagation()}>{it.itemName}</span>
          </div>
        ))}
        {
          prodId !== TravelPlanType.Domestic &&
            <div className="search">
              <input type="text" className="search-input" placeholder="請點此進行選擇(最多6項)，或輸入部份地名進行搜尋" value={keyword} onClick={handleSearchClick} onChange={handleSearchChange} disabled={disabled} />
              <i className="fa fa-times-circle" onClick={handleSearchClear} />
            </div>
        }
        {/* 選擇面版 */}
        <div className={'panel' + (isOpen ? ' panel--active' : '')} onClick={(e) => e.stopPropagation()}>
          <ul className={'select-header' + (keyword || tabOptions.length === 0 ? ' searching' : '')}>
            {tabOptions.map((it, index) => (
              <li key={`tab-${index}`} className={`tab ${it.value === currentTab ? ' tab--active' : ''}`} onClick={e => handleTabClick(e, it.value)}>{it.text}</li>
            ))}
          </ul>
          <hr className={'sep' + (keyword || tabOptions.length === 0 ? ' searching' : '')} />
          <ul className={'container' + (keyword || tabOptions.length === 0 ? ' searching' : '')}>
            {groups.map(group => {
              return (
                <div key={group.groupId} className={'page' + (group.groupId === currentTab ? ' page--active' : '')}>
                  <li className="grid-wrapper">
                    {group.items.map(it => {
                      return (
                        <div key={it.itemId} className="grid-box">
                          <CheckItem groupId={it.groupId} itemId={it.itemId} label={it.itemName} isChecked={it.isChecked} onClick={() => handleItemClick(it.groupId, it.itemId)} />
                        </div>
                      );
                    })}
                  </li>
                </div>
              );
            })}
          </ul>
          <ul className={'result' + (keyword ? ' searching' : '')}>
            {result.length > 0 ? (
              <li className="grid-wrapper">
                {result.map(it => {
                  return (
                    <div key={it.itemId} className="grid-box">
                      <CheckItem groupId={it.groupId} itemId={it.itemId} label={it.itemName} isChecked={it.isChecked} onClick={() => handleItemClick(it.groupId, it.itemId)} />
                    </div>
                  );
                })}
              </li>
            ) : (
              <div className="no-match-result">
                沒有符合的結果
              </div>
            )}
          </ul>
        </div>
      </div>
      {children}
    </>
  );
};

export default memo(FilterSelect);
